import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Instagram, MessageCircle, ArrowUpRight } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "./BotanicalBackdrop";
import { BrandLogo } from "./BrandMark";
import { LEGAL_LINKS } from "@/pages/LegalPage";

export const Footer = ({ onEnquire }) => {
  return (
    <footer id="contact" data-testid="site-footer" className="relative bg-[#141914] text-[#EAE2D2] overflow-hidden mt-auto">
      <BotanicalBackdrop variant="sideLeft" />

      {/* CTA band */}
      <div className="relative border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="label-eyebrow !text-[var(--hk-gold)]">Begin Your Ritual</div>
            <h2 className="serif mt-3 text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.05] text-white">
              Wellness begins with <span className="italic-accent text-[var(--hk-orange)]">better choices.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-[1.85] text-white/70 max-w-xl">
              Discover HerbKranti's growing collection — thoughtfully sourced, beautifully crafted, and inspired by the botanical heritage of Ayurveda.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-4 lg:justify-end">
            <Link to="/products" className="hk-btn-primary" data-testid="footer-shop-btn">
              Shop the Collection
            </Link>
            <a
              href="https://wa.me/917249797972"
              target="_blank"
              rel="noreferrer"
              className="hk-btn-ghost !border-white/20 !text-white hover:!bg-white/5 hover:!text-white"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle size={16} /> WhatsApp us
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Link to="/" aria-label="HerbKranti — home" className="inline-flex">
            <BrandLogo variant="dark" markSize={44} wordSize={22} showTagline={true} taglineSize={9} testId="footer-brand-logo" />
          </Link>
          <p className="mt-6 text-[13.5px] text-white/60 leading-[1.85] max-w-xs">
            A premium Indian wellness brand — thoughtfully selected, beautifully crafted, and inspired by the botanical heritage of Ayurveda.
          </p>
        </div>

        <div>
          <div className="label-eyebrow !text-[var(--hk-gold)]">Explore</div>
          <ul className="mt-5 space-y-3 text-[14px] text-white/70">
            {[
              { label: "Home", href: "/" },
              { label: "Our Story", href: "/our-story" },
              { label: "Products", href: "/products" },
              { label: "Reviews", href: "/reviews" },
              { label: "FAQ", href: "/faq" },
              { label: "Track Order", href: "/track-order" },
              { label: "Contact", href: "/contact" },
            ].map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  className="hover:text-[var(--hk-orange)] transition-colors duration-200"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="label-eyebrow !text-[var(--hk-gold)]">Contact</div>
          <ul className="mt-5 space-y-4 text-[14px] text-white/80">
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-1 text-[var(--hk-gold)]" />
              <a href="tel:+917249797972" className="hover:text-[var(--hk-orange)] transition-colors" data-testid="footer-phone">
                +91 72497 97972
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-1 text-[var(--hk-gold)]" />
              <a href="mailto:herbkranti@gmail.com" className="hover:text-[var(--hk-orange)] transition-colors" data-testid="footer-email">
                herbkranti@gmail.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Instagram size={16} className="mt-1 text-[var(--hk-gold)]" />
              <a
                href="https://instagram.com/herbkranti"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[var(--hk-orange)] transition-colors inline-flex items-center gap-1"
                data-testid="footer-instagram"
              >
                @herbkranti <ArrowUpRight size={12} />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <div className="label-eyebrow !text-[var(--hk-gold)]">The Craft</div>
          <p className="mt-5 text-[13.5px] text-white/60 leading-[1.75]">
            Every HerbKranti product is designed as an everyday ritual — inspired by nature, refined for modern life.
          </p>
          <div className="mt-5 flex items-center gap-2 text-[var(--hk-gold)]">
            <LeafGlyph />
            <span className="text-[12px] tracking-[0.2em] uppercase">Since Nature</span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-white/60" data-testid="footer-legal-links">
          <span className="label-eyebrow !text-[var(--hk-gold)] !text-[10px]">Legal</span>
          {LEGAL_LINKS.map((l) => (
            <Link key={l.slug} to={l.href} className="hover:text-[var(--hk-orange)] transition-colors duration-200" data-testid={`footer-legal-${l.slug}`}>
              {l.label}
            </Link>
          ))}
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>© {new Date().getFullYear()} HerbKranti. All rights reserved.</div>
          <div className="text-[11px] tracking-wide max-w-xl md:text-right">
            Educational information only. Products are not intended to diagnose, treat, cure or prevent any disease.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
